// src/pages/AdminPanel.tsx
// Status Board specific admin panel for managing rooms, investigators, and settings
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import api from '../services/api';
import {
  Upload, Settings, Home, LogOut, Plus, Edit2,
  Save, X, AlertCircle, CheckCircle, Eye, EyeOff,
  Stethoscope, LayoutGrid, Trash2
} from 'lucide-react';
import SiteSelector from '../components/SiteSelector';
import { ThemeToggle } from '../theme/ThemeToggle';
import './AdminPanel.css';

interface Room {
  id: string;
  room_name: string;
  is_active: boolean;
}

interface Investigator {
  id: string;
  site_id: string;
  name: string;
  display_order: number;
  is_active: boolean;
}

interface ColorThresholds {
  green: number;
  yellow: number;
  red: number;
}

const AdminPanel: React.FC = () => {
  const { currentSite, user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Entitlement check state
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);
  const [entitlementChecked, setEntitlementChecked] = useState(false);

  const [activeTab, setActiveTab] = useState('upload');
  const [rooms, setRooms] = useState<Room[]>([]);
  const [colorThresholds, setColorThresholds] = useState<ColorThresholds>({
    green: 15,
    yellow: 30,
    red: 45
  });
  const [newRoom, setNewRoom] = useState('');
  const [editingRoom, setEditingRoom] = useState<string | null>(null);
  const [editingRoomName, setEditingRoomName] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [loading, setLoading] = useState(false);

  // Investigator management
  const [investigators, setInvestigators] = useState<Investigator[]>([]);
  const [newInvestigator, setNewInvestigator] = useState('');
  const [editingInvestigator, setEditingInvestigator] = useState<string | null>(null);
  const [editingInvestigatorName, setEditingInvestigatorName] = useState('');

  useEffect(() => {
    if (currentSite) {
      fetchRooms();
      fetchSettings();
      fetchInvestigators();
    }
  }, [currentSite?.id]);

  // Check Status Board entitlement when component mounts or site changes
  useEffect(() => {
    const checkEntitlement = async () => {
      // Super admins always have access
      if (user?.role === 'super_admin') {
        setHasEntitlement(true);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      if (!currentSite) {
        // No site selected = no access to tools
        setHasEntitlement(false);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      try {
        const response = await api.get('/api/my-tools');
        const tools = response.data || [];
        const statusBoardTool = tools.find((t: any) => t.tool_id === 'status-board');
        setHasEntitlement(statusBoardTool?.is_available === true);
        setEntitlementChecked(true);
      } catch (error) {
        console.error('Error checking Status Board entitlement:', error);
        setHasEntitlement(false);
        setEntitlementChecked(true);
      } finally {
        setCheckingEntitlement(false);
      }
    };

    checkEntitlement();
  }, [user?.role, currentSite]);

  const fetchRooms = async () => {
    try {
      const response = await api.get('/api/rooms');
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/settings');
      if (response.data.color_thresholds) {
        setColorThresholds(response.data.color_thresholds);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchInvestigators = async () => {
    if (!currentSite) return;
    try {
      const response = await api.get(`/api/sites/${currentSite.id}/investigators`);
      setInvestigators(response.data);
    } catch (error) {
      console.error('Error fetching investigators:', error);
    }
  };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) return;

    setLoading(true);
    setUploadStatus(null);

    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const response = await api.post('/api/upload-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadStatus({
        type: 'success',
        message: response.data.message || 'File uploaded successfully'
      });
      setUploadFile(null);
    } catch (error: any) {
      setUploadStatus({
        type: 'error',
        message: error.response?.data?.error || 'Error uploading file'
      });
    } finally {
      setLoading(false);
    }
  };

  const addRoom = async () => {
    if (!newRoom.trim()) return;

    try {
      await api.post('/api/rooms', { room_name: newRoom });
      setNewRoom('');
      fetchRooms();
    } catch (error) {
      console.error('Error adding room:', error);
      alert('Error adding room');
    }
  };

  const updateRoom = async (roomId: string) => {
    if (!editingRoomName.trim()) return;

    try {
      await api.patch(`/api/rooms/${roomId}`, { room_name: editingRoomName });
      setEditingRoom(null);
      fetchRooms();
    } catch (error) {
      console.error('Error updating room:', error);
      alert('Error updating room');
    }
  };

  const toggleRoomActive = async (roomId: string, currentStatus: boolean) => {
    try {
      await api.patch(`/api/rooms/${roomId}`, { is_active: !currentStatus });
      fetchRooms();
    } catch (error) {
      console.error('Error toggling room status:', error);
      alert('Error updating room status');
    }
  };

  const updateColorThresholds = async () => {
    try {
      await api.patch('/api/settings', {
        setting_key: 'color_thresholds',
        setting_value: colorThresholds
      });
      alert('Settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Error updating settings');
    }
  };

  // Investigator management functions
  const addInvestigator = async () => {
    if (!newInvestigator.trim() || !currentSite) return;

    try {
      await api.post(`/api/sites/${currentSite.id}/investigators`, {
        name: newInvestigator,
        display_order: investigators.length
      });
      setNewInvestigator('');
      fetchInvestigators();
    } catch (error) {
      console.error('Error adding investigator:', error);
      alert('Error adding investigator');
    }
  };

  const updateInvestigator = async (invId: string) => {
    if (!editingInvestigatorName.trim() || !currentSite) return;

    try {
      await api.patch(`/api/sites/${currentSite.id}/investigators/${invId}`, {
        name: editingInvestigatorName
      });
      setEditingInvestigator(null);
      fetchInvestigators();
    } catch (error) {
      console.error('Error updating investigator:', error);
      alert('Error updating investigator');
    }
  };

  const deleteInvestigator = async (invId: string) => {
    if (!currentSite || !window.confirm('Are you sure you want to delete this investigator?')) return;

    try {
      await api.delete(`/api/sites/${currentSite.id}/investigators/${invId}`);
      fetchInvestigators();
    } catch (error) {
      console.error('Error deleting investigator:', error);
      alert('Error deleting investigator');
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  // Show loading while checking entitlement
  if (checkingEntitlement) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Show Access Denied if user doesn't have Status Board entitlement
  if (entitlementChecked && !hasEntitlement && user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You do not have access to the Status Board for your current site.
            Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-sidebar">
        <div className="sidebar-logo" onClick={() => navigate('/')}>
          <span className="logo-icon">&#9678;</span>
          SiteOperations.io
        </div>
        <h2>Status Board Admin</h2>
        <div className="sidebar-site-selector">
          <SiteSelector />
        </div>
        <nav className="admin-nav">
          <button
            className={activeTab === 'upload' ? 'active' : ''}
            onClick={() => setActiveTab('upload')}
          >
            <Upload size={16} />
            Upload CSV
          </button>
          <button
            className={activeTab === 'rooms' ? 'active' : ''}
            onClick={() => setActiveTab('rooms')}
          >
            <Home size={16} />
            Manage Rooms
          </button>
          <button
            className={activeTab === 'investigators' ? 'active' : ''}
            onClick={() => setActiveTab('investigators')}
          >
            <Stethoscope size={16} />
            Investigators
          </button>
          <button
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={16} />
            Settings
          </button>
        </nav>
        <div className="sidebar-footer">
          <button onClick={() => navigate('/')} className="nav-button">
            <LayoutGrid size={16} />
            Tools
          </button>
          <button onClick={() => navigate('/status-board')} className="nav-button">
            <Home size={16} />
            Status Board
          </button>
          <button onClick={handleSignOut} className="nav-button">
            <LogOut size={16} />
            Sign Out
          </button>
          <ThemeToggle variant="neutral" className="sidebar-theme-toggle" />
        </div>
      </div>

      <div className="admin-content">
        {/* Upload CSV Tab */}
        {activeTab === 'upload' && (
          <div className="tab-content">
            <h3>Upload Daily CSV {currentSite && `- ${currentSite.name}`}</h3>
            <form onSubmit={handleFileUpload} className="upload-form">
              <div className="file-input-wrapper">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  id="csv-file"
                />
                <label htmlFor="csv-file" className="file-label">
                  {uploadFile ? uploadFile.name : 'Choose CSV file...'}
                </label>
              </div>

              {uploadStatus && (
                <div className={`status-message ${uploadStatus.type}`}>
                  {uploadStatus.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                  {uploadStatus.message}
                </div>
              )}

              <button type="submit" className="upload-button" disabled={!uploadFile || loading}>
                {loading ? 'Uploading...' : 'Upload File'}
              </button>
            </form>

            <div className="info-section">
              <h4>CSV Format Requirements:</h4>
              <ul>
                <li>Subject ID: Unique patient identifier</li>
                <li>Initials: Patient initials</li>
                <li>Protocol Number: Study/protocol name</li>
                <li>Scheduled Time: Appointment date/time</li>
                <li>Name: Visit type/name</li>
                <li>Full Name: CRC full name (first name will be used)</li>
              </ul>
              <p className="warning">
                <AlertCircle size={16} />
                Uploading a new CSV will replace all appointments for today at this site
              </p>
            </div>
          </div>
        )}

        {/* Rooms Tab */}
        {activeTab === 'rooms' && (
          <div className="tab-content">
            <h3>Manage Rooms {currentSite && `- ${currentSite.name}`}</h3>
            <div className="add-room">
              <input
                type="text"
                value={newRoom}
                onChange={(e) => setNewRoom(e.target.value)}
                placeholder="New room name..."
                onKeyPress={(e) => e.key === 'Enter' && addRoom()}
              />
              <button onClick={addRoom} className="add-button">
                <Plus size={16} />
                Add Room
              </button>
            </div>

            <div className="rooms-list">
              {rooms.map(room => (
                <div key={room.id} className={`room-item ${!room.is_active ? 'inactive' : ''}`}>
                  {editingRoom === room.id ? (
                    <>
                      <input
                        type="text"
                        value={editingRoomName}
                        onChange={(e) => setEditingRoomName(e.target.value)}
                        className="edit-input"
                      />
                      <button onClick={() => updateRoom(room.id)} className="icon-button">
                        <Save size={16} />
                      </button>
                      <button onClick={() => setEditingRoom(null)} className="icon-button">
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="room-name">{room.room_name}</span>
                      <div className="room-actions">
                        <button
                          onClick={() => {
                            setEditingRoom(room.id);
                            setEditingRoomName(room.room_name);
                          }}
                          className="icon-button"
                        >
                          <Edit2 size={16} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Investigators Tab */}
        {activeTab === 'investigators' && (
          <div className="tab-content">
            <h3>Manage Investigators {currentSite && `- ${currentSite.name}`}</h3>
            <div className="add-room">
              <input
                type="text"
                value={newInvestigator}
                onChange={(e) => setNewInvestigator(e.target.value)}
                placeholder="New investigator name..."
                onKeyPress={(e) => e.key === 'Enter' && addInvestigator()}
              />
              <button onClick={addInvestigator} className="add-button">
                <Plus size={16} />
                Add Investigator
              </button>
            </div>

            <div className="rooms-list">
              {investigators.map(inv => (
                <div key={inv.id} className="room-item">
                  {editingInvestigator === inv.id ? (
                    <>
                      <input
                        type="text"
                        value={editingInvestigatorName}
                        onChange={(e) => setEditingInvestigatorName(e.target.value)}
                        className="edit-input"
                      />
                      <button onClick={() => updateInvestigator(inv.id)} className="icon-button">
                        <Save size={16} />
                      </button>
                      <button onClick={() => setEditingInvestigator(null)} className="icon-button">
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="room-name">{inv.name}</span>
                      <div className="room-actions">
                        <button
                          onClick={() => {
                            setEditingInvestigator(inv.id);
                            setEditingInvestigatorName(inv.name);
                          }}
                          className="icon-button"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => deleteInvestigator(inv.id)}
                          className="icon-button danger"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {investigators.length === 0 && (
                <p className="empty-message">No investigators configured for this site.</p>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="tab-content">
            <h3>Site Settings {currentSite && `- ${currentSite.name}`}</h3>
            <div className="settings-section">
              <h4>Color Thresholds (minutes)</h4>
              <p className="settings-description">
                Set the time thresholds for color-coding patient wait times
              </p>

              <div className="threshold-inputs">
                <div className="threshold-group">
                  <label>
                    <span className="color-indicator green"></span>
                    Green (Normal)
                  </label>
                  <input
                    type="number"
                    value={colorThresholds.green}
                    onChange={(e) => setColorThresholds({ ...colorThresholds, green: parseInt(e.target.value) })}
                    min="1"
                    max="60"
                  />
                  <span className="unit">minutes</span>
                </div>

                <div className="threshold-group">
                  <label>
                    <span className="color-indicator yellow"></span>
                    Yellow (Warning)
                  </label>
                  <input
                    type="number"
                    value={colorThresholds.yellow}
                    onChange={(e) => setColorThresholds({ ...colorThresholds, yellow: parseInt(e.target.value) })}
                    min="1"
                    max="120"
                  />
                  <span className="unit">minutes</span>
                </div>

                <div className="threshold-group">
                  <label>
                    <span className="color-indicator red"></span>
                    Red (Critical)
                  </label>
                  <input
                    type="number"
                    value={colorThresholds.red}
                    onChange={(e) => setColorThresholds({ ...colorThresholds, red: parseInt(e.target.value) })}
                    min="1"
                    max="180"
                  />
                  <span className="unit">minutes</span>
                </div>
              </div>

              <button onClick={updateColorThresholds} className="save-button">
                <Save size={16} />
                Save Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
